import React, {useCallback, useEffect, useMemo, useRef, useState} from "react"
import {useIntl} from "react-intl"
import {getAcademicYears, getCurrentQuarterRange, isFuture, toDateString} from "./academicCalendar"
import "./DateRangeSelector.scss"

function formatRangeDisplay(from, to, locale) {
    const sameYear = from.getFullYear() === to.getFullYear()
    const sameMonth = sameYear && from.getMonth() === to.getMonth()

    // Check if a date is the first/last day of its month
    const isMonthStart = (d) => d.getDate() === 1
    const isMonthEnd = (d) => d.getDate() === new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()

    if (sameMonth) {
        // Whole month: "Oct 2025"
        if (isMonthStart(from) && isMonthEnd(to)) {
            return from.toLocaleDateString(locale, {month: "short", year: "numeric"})
        }
        // Partial month: "15 – 18 Oct 2025"
        return `${from.getDate()} – ${to.getDate()} ${from.toLocaleDateString(locale, {month: "short"})} ${from.getFullYear()}`
    }

    if (sameYear) {
        // Whole months, same year: "Apr – Jun 2026"
        if (isMonthStart(from) && isMonthEnd(to)) {
            const fromMonth = from.toLocaleDateString(locale, {month: "short"})
            const toMonth = to.toLocaleDateString(locale, {month: "short"})
            return `${fromMonth} – ${toMonth} ${from.getFullYear()}`
        }
        // Partial or mixed, same year: "15 Oct – 3 Nov 2025"
        const fmtDay = (d) => `${d.getDate()} ${d.toLocaleDateString(locale, {month: "short"})}`
        return `${fmtDay(from)} – ${fmtDay(to)} ${from.getFullYear()}`
    }

    // Cross-year, whole months: "Oct 2025 – Mar 2026"
    if (isMonthStart(from) && isMonthEnd(to)) {
        const fmt = (d) => d.toLocaleDateString(locale, {month: "short", year: "numeric"})
        return `${fmt(from)} – ${fmt(to)}`
    }
    // Cross-year, partial: "15 Oct 2025 – 3 Mar 2026"
    const fmtFull = (d) => `${d.getDate()} ${d.toLocaleDateString(locale, {month: "short", year: "numeric"})}`
    return `${fmtFull(from)} – ${fmtFull(to)}`
}

function formatQuarterLabel(start, end, locale) {
    const startMonth = start.toLocaleDateString(locale, {month: "short"})
    const endMonth = end.toLocaleDateString(locale, {month: "short"})
    return `${startMonth}–${endMonth} ${start.getFullYear()}`
}

/**
 * A dropdown date-range selector with academic-year and quarter presets.
 *
 * Props:
 *   value   — { dateFrom: "YYYY-MM-DD" | null, dateTo: "YYYY-MM-DD" | null }
 *   onChange — called with { dateFrom, dateTo } on every selection
 */
export default function DateRangeSelector({value, onChange}) {
    const intl = useIntl()
    const [isOpen, setIsOpen] = useState(false)
    const ref = useRef(null)
    const [manualInputs, setManualInputs] = useState({
        start: value?.dateFrom || "",
        end: value?.dateTo || "",
    })

    // Sync manual inputs when value is changed externally (e.g. parent reset)
    useEffect(() => {
        setManualInputs({
            start: value?.dateFrom || "",
            end: value?.dateTo || "",
        })
    }, [value?.dateFrom, value?.dateTo])

    // Close on click outside; commit manual input if both fields are filled
    const handleMouseDown = useCallback((e) => {
        if (ref.current && !ref.current.contains(e.target)) {
            if (manualInputs.start && manualInputs.end) {
                onChange({dateFrom: manualInputs.start, dateTo: manualInputs.end})
            }
            setIsOpen(false)
        }
    }, [manualInputs, onChange])

    useEffect(() => {
        document.addEventListener("mousedown", handleMouseDown)
        return () => document.removeEventListener("mousedown", handleMouseDown)
    }, [handleMouseDown])

    const academicYears = useMemo(() => {
        return getAcademicYears(3).map(ay => ({
            ...ay,
            quarters: ay.quarters.map(q => ({
                ...q,
                label: formatQuarterLabel(q.start, q.end, intl.locale),
            })),
        }))
    }, [intl.locale])

    const handleSelect = useCallback((dateFrom, dateTo) => {
        onChange({dateFrom, dateTo})
        setIsOpen(false)
    }, [onChange])

    const handleAcademicYearSelect = useCallback((ay) => {
        handleSelect(toDateString(ay.start), toDateString(ay.end))
    }, [handleSelect])

    const handleQuarterSelect = useCallback((q) => {
        handleSelect(toDateString(q.start), toDateString(q.end))
    }, [handleSelect])

    const handleManualInputChange = useCallback((startVal, endVal) => {
        const next = {
            start: startVal !== null ? startVal : manualInputs.start,
            end: endVal !== null ? endVal : manualInputs.end,
        }
        setManualInputs(next)
        if (next.start && next.end && new Date(next.start) <= new Date(next.end)) {
            onChange({dateFrom: next.start, dateTo: next.end})
        }
    }, [manualInputs, onChange])

    const handleReset = useCallback(() => {
        const qr = getCurrentQuarterRange()
        const dateFrom = toDateString(qr.start)
        const dateTo = toDateString(qr.end)
        onChange({dateFrom, dateTo})
        setManualInputs({start: dateFrom, end: dateTo})
        setIsOpen(false)
    }, [onChange])

    // Format the closed-box display value
    const displayValue = value?.dateFrom && value?.dateTo
        ? formatRangeDisplay(new Date(value.dateFrom), new Date(value.dateTo), intl.locale)
        : intl.formatMessage({id: "analysis.selectDateRange", defaultMessage: "Select date range"})

    return (
        <div className={`date-range-selector${value?.dateFrom ? " active" : ""}`} ref={ref}>
            <button
                className="selector-box"
                onClick={() => setIsOpen(o => !o)}
                aria-expanded={isOpen}
            >
                {displayValue}
            </button>

            {isOpen && (
                <div className="selector-dropdown">
                    <div className="custom-range">
                        <input
                            type="date"
                            value={manualInputs.start}
                            onChange={e => handleManualInputChange(e.target.value || "", null)}
                            aria-label={intl.formatMessage({id: "dateRange.startDate", defaultMessage: "Start date"})}
                        />
                        <span>–</span>
                        <input
                            type="date"
                            value={manualInputs.end}
                            onChange={e => handleManualInputChange(null, e.target.value || "")}
                            aria-label={intl.formatMessage({id: "dateRange.endDate", defaultMessage: "End date"})}
                        />
                    </div>

                    <ul>
                        {academicYears.map(ay => (
                            <li key={ay.label} className="academic-year">
                                <div
                                    className="year-header"
                                    onClick={() => handleAcademicYearSelect(ay)}
                                >
                                    {ay.label}
                                </div>
                                {ay.quarters.map((q, idx) => {
                                    const future = isFuture(q.start)
                                    return (
                                        <div
                                            key={idx}
                                            className={`quarter${future ? " disabled" : ""}`}
                                            onClick={() => !future && handleQuarterSelect(q)}
                                            title={future
                                                ? intl.formatMessage({id: "dateRange.futureQuarter", defaultMessage: "Future quarters are not available"})
                                                : undefined
                                            }
                                        >
                                            {q.label}
                                        </div>
                                    )
                                })}
                            </li>
                        ))}
                    </ul>

                    <button className="reset-btn" onClick={handleReset}>
                        {intl.formatMessage({id: "analysis.resetToCurrentQuarter", defaultMessage: "Reset to current quarter"})}
                    </button>
                </div>
            )}
        </div>
    )
}
